import React, { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { requestPasswordResetSchema } from "@tyto/shared";

type ForgotPasswordFormValues = z.infer<typeof requestPasswordResetSchema>;

const ForgotPassword: React.FC = () => {
  const [isSuccess, setIsSuccess] = useState(false);

  const { mutate: requestPasswordReset, isPending, isError, error } = useMutation({
    mutationKey: ['forgotPassword'],
    mutationFn: async (data: ForgotPasswordFormValues) => {
      const res = await fetch('/api/auth/reset-password-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        // For debugging - log the actual response
        console.error('Failed to reset password:', await res.text().catch(() => 'No response body'));
        throw new Error('Failed to request password reset');
      }

      // If successful
      setIsSuccess(true);
      return res.json();
    }
  });

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(requestPasswordResetSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = (data: ForgotPasswordFormValues) => {
    requestPasswordReset(data);
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-12rem)] px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Forgot Password</CardTitle>
          <CardDescription>
            Enter your email address and we'll send you a link to reset your password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isSuccess ? (
            <div className="mb-4 p-4 rounded-md bg-green-50 border border-green-200">
              <div className="flex items-center">
                <CheckCircle2 className="h-5 w-5 text-green-600 mr-2" />
                <p className="font-semibold text-green-800">Check your email</p>
              </div>
              <p className="mt-2 text-green-700">
                If an account exists with that email, we've sent instructions to reset your password.
              </p>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="your.email@example.com" type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {isError && (
                  <div className="p-4 rounded-md bg-red-50 border border-red-200 text-red-800">
                    <div className="flex items-center">
                      <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                      <p className="font-semibold text-md">Error</p>
                    </div>
                    <p className="mt-2 text-sm">
                      {error?.message || 'Something went wrong. Please try again.'}
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isPending}
                >
                  {isPending ? 'Sending...' : 'Reset Password'}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          <Link to="/login" className="text-sm text-blue-600 hover:text-blue-800">
            Back to login
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ForgotPassword;
